import api from '@flatfile/api';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const extractValuesFromFlatfileData = (valueObj) =>
	Object.keys(valueObj).reduce(
		(acc, key) => ({
			...acc,
			[key]: valueObj[key]['value'],
		}),
		{}
	);

export const FLATFILE_SUBMIT_ACTION_NAME = 'submitAction';

export const WORK_BOOK_SUBMIT_ACTION = {
	operation: FLATFILE_SUBMIT_ACTION_NAME,
	mode: 'foreground',
	label: 'Submit',
	description: 'Submit data',
	primary: true,
};

export const FLATFILE_REGEX_VALUES = {
	DATE_FORMAT: `[0-9]{2}/[0-9]{2}/[0-9]{4}`,
	COMMA_SEPARATED_FORMAT: '[a-zA-Z0-9_ :]+(,[a-zA-Z0-9_ :]+)*$',
	NUMBER_FORMAT: '^\\d+(\\.\\d+)?$',
};

export const validateRegexForFlatfileRecord = (record, fieldName, regexPattern, message) => {
	const regex = new RegExp(regexPattern);
	const field = record.get(fieldName);
	if (field && !regex.test(field)) {
		record.addError(fieldName, message);
	}
};

export const REGEX_CONFIG_NAMES = {
	TRANSFER_DATE: 'TRANSFER_DATE',
	EFFECTIVE_DATE: 'EFFECTIVE_DATE',
	SIMPLE_DATE: 'SIMPLE_DATE',
	INVOICE_DATE: 'INVOICE_DATE',
	MULTIPLE_CHOICE_VALUES: 'MULTIPLE_CHOICE_VALUES',
	NUMBER_VALUE: 'NUMBER_VALUE',
};

const DEFAULT_REGEX_RECORD_HOOK_CONFIGS = {
	[REGEX_CONFIG_NAMES.TRANSFER_DATE]: {
		fieldName: 'transferDate',
		regexPattern: FLATFILE_REGEX_VALUES.DATE_FORMAT,
		message: 'Transfer date should be in MM/DD/yyyy format',
	},
	[REGEX_CONFIG_NAMES.EFFECTIVE_DATE]: {
		fieldName: 'effectiveDate',
		regexPattern: FLATFILE_REGEX_VALUES.DATE_FORMAT,
		message: 'Effective date should be in MM/DD/yyyy format',
	},
	[REGEX_CONFIG_NAMES.SIMPLE_DATE]: {
		fieldName: 'date',
		regexPattern: FLATFILE_REGEX_VALUES.DATE_FORMAT,
		message: 'Date should be in MM/DD/yyyy format',
	},
	[REGEX_CONFIG_NAMES.INVOICE_DATE]: {
		fieldName: 'invoiceDate',
		regexPattern: FLATFILE_REGEX_VALUES.DATE_FORMAT,
		message: 'Invoice date should be in MM/DD/yyyy format',
	},
	[REGEX_CONFIG_NAMES.MULTIPLE_CHOICE_VALUES]: {
		fieldName: 'multipleChoicesWithLabel',
		regexPattern: FLATFILE_REGEX_VALUES.COMMA_SEPARATED_FORMAT,
		message: 'Values should be comma separated. Ex: 1:low,2:high',
	},
	[REGEX_CONFIG_NAMES.NUMBER_VALUE]: {
		fieldName: 'numberValue',
		regexPattern: FLATFILE_REGEX_VALUES.NUMBER_FORMAT,
		message: 'Value should be a valid number.',
	},
};

export const regexRecordHook =
	(configNames = []) =>
	(record) => {
		configNames.map((configName) => {
			const config = DEFAULT_REGEX_RECORD_HOOK_CONFIGS[configName];
			if (config) {
				const { fieldName, regexPattern, message } = config;
				validateRegexForFlatfileRecord(record, fieldName, regexPattern, message);
			}
		});

		return record;
	};

export const requiredWithValueRecordHook =
	(requiredFieldNames: any[], enumFieldName, enumValues: any[]) => (record) => {
		const allRequiredFieldsNotEntered = !!requiredFieldNames.find((field) => !record.get(field));

		const enumFieldValue = record.get(enumFieldName);

		if (enumFieldValue && enumValues.includes(enumFieldValue) && allRequiredFieldsNotEntered) {
			requiredFieldNames.map((field) => {
				record.addError(field, `${field} is required with value ${enumFieldValue}`);
			});
		}

		return record;
	};

export const resetFieldWithValueRecordHook =
	(resetFieldNames: any[], resetValue = '', enumFieldName, enumValues: any[]) =>
	(record) => {
		const enumFieldValue = record.get(enumFieldName);

		if (enumFieldValue && enumValues.includes(enumFieldValue)) {
			resetFieldNames.map((fieldName) => {
				record.set(fieldName, resetValue);
			});
		}

		return record;
	};

export const numberSanitizerRecordHook = (fieldNames: string[]) => (record) => {
	fieldNames.forEach((field) => {
		const value = record.get(field);

		if (!value) return;

		const cleaned = value.toString().replace(/[^0-9.]/g, '');

		if (cleaned === '' || isNaN(Number(cleaned))) return;

		record.set(field, Number(cleaned));
	});

	return record;
};

export const getFlatfileInputRecordsFromData = (data) =>
	data.map((record) =>
		Object.keys(record).reduce(
			(acc, key) => ({
				...acc,
				[key]: {
					value: record[key],
					messages: [],
					valid: true,
				},
			}),
			{}
		)
	);

export const submitData = async (event, onDataLoad, onSuccess, validateRecordsOnSubmit?) => {
	const {
		context: { jobId, workbookId },
	} = event;
	const { data: workbook } = await api.workbooks.get(workbookId);

	await api.jobs.ack(jobId, {
		info: 'Getting started.',
		progress: 10,
	});

	const sheetId = nullSafeGet('sheets.0.id', workbook);

	const { data: updatedData } = await api.records.get(sheetId);

	const records = updatedData.records;

	const invalidData = records.find((_) => !_.valid);

	if (!!invalidData) {
		await api.jobs.fail(jobId, {
			outcome: {
				message: 'Fix errors before submitting!',
			},
		});

		return;
	}

	const validData = records
		.filter((_) => _.valid)
		.map((_) => extractValuesFromFlatfileData(_.values));

	const validationErrorMessage = validateRecordsOnSubmit && validateRecordsOnSubmit(validData);
	if (validationErrorMessage) {
		await api.jobs.fail(jobId, {
			outcome: {
				message: validationErrorMessage,
			},
		});

		return;
	}

	onDataLoad({ validData })
		.then(async (message) => {
			await api.jobs.complete(jobId, {
				outcome: {
					message,
				},
			});
			onSuccess();
		})
		.catch(async (message) => {
			await api.jobs.fail(jobId, {
				outcome: {
					message,
				},
			});
		});
};
